import React, { useEffect, useState } from "react";
import Sidebar from "../../menu/Sidebar";
import Topbar from "../../menu/Topbar";
import Api from "../../Services/Api";
import RealgemsSidebar from "../../realgems/RealgemsSidebar";
import "./VideosApoio.css";

const getVideoLink = (video) => String(video?.linkUrl || video?.url || "").trim();

const downloadFile = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename || "";
  link.target = "_blank";
  link.rel = "noreferrer";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const VideosApoio = ({ audience = "LICENSED", realgems = false }) => {
  const [menuClose, setMenuClose] = useState(false);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState("");

  const toggleMenu = () => {
    setMenuClose((prev) => !prev);
  };

  useEffect(() => {
    let mounted = true;

    const loadVideos = async () => {
      try {
        setLoading(true);
        const response = await Api.get("/public/machine-videos", {
          params: { audience },
        });
        const items = Array.isArray(response?.data?.items) ? response.data.items : [];

        if (!mounted) return;
        setVideos(items);
      } catch (err) {
        console.error("Erro ao carregar videos de apoio:", err);

        if (!mounted) return;
        setVideos([]);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadVideos();

    return () => {
      mounted = false;
    };
  }, [audience]);

  useEffect(() => {
    if (!feedback) return undefined;

    const timeoutId = window.setTimeout(() => {
      setFeedback("");
    }, 3000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [feedback]);

  const handleShare = async (video) => {
    const link = getVideoLink(video);

    if (!link) {
      setFeedback("Nao encontramos um link valido para compartilhar.");
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: video.title || "Video de apoio",
          text: `Confira este video de apoio: ${video.title || ""}`.trim(),
          url: link,
        });
        setFeedback("Link compartilhado com sucesso.");
        return;
      }

      await navigator.clipboard.writeText(link);
      setFeedback("Link copiado para compartilhamento.");
    } catch (err) {
      console.error("Erro ao compartilhar video:", err);

      try {
        await navigator.clipboard.writeText(link);
        setFeedback("Link copiado para compartilhamento.");
      } catch (clipboardErr) {
        console.error("Erro ao copiar link do video:", clipboardErr);
        setFeedback("Nao foi possivel compartilhar agora.");
      }
    }
  };

  const handleDownload = (video) => {
    if (video?.sourceType === "UPLOAD" && video?.url) {
      downloadFile(video.url, video.originalName || `${video.title || "video"}.mp4`);
      return;
    }

    const link = getVideoLink(video);

    if (!link) {
      setFeedback("Nao encontramos um link valido para abrir o video.");
      return;
    }

    window.open(link, "_blank", "noopener,noreferrer");
    setFeedback("Video aberto no YouTube para compartilhar ou baixar no app.");
  };

  return (
    <div className="app-container">
      {realgems ? (
        <RealgemsSidebar close={menuClose} videosActive="active" />
      ) : (
        <Sidebar close={menuClose} videosApoioActive="active" />
      )}

      <main className="main-content">
        <Topbar page="Videos de apoio" onToggleMenu={toggleMenu} />

        <section className="support-videos-card">
          <div className="support-videos-header">
            <div>
              <h1 className="support-videos-title">Videos de apoio</h1>
            </div>

            {feedback ? <div className="support-videos-feedback">{feedback}</div> : null}
          </div>

          {loading ? (
            <div className="support-videos-empty-state">Carregando videos de apoio...</div>
          ) : videos.length ? (
            <div className="support-videos-grid">
              {videos.map((video) => {
                const isLink = video.sourceType === "LINK";
                const previewClassName = `support-videos-preview ${
                  video.aspectRatio === "9:16" ? "is-vertical" : ""
                }`;

                return (
                  <article className="support-video-item" key={video.id}>
                    <div className={previewClassName}>
                      {isLink ? (
                        <iframe
                          src={video.embedUrl}
                          title={video.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          allowFullScreen
                        />
                      ) : (
                        <video controls preload="metadata" src={video.url}>
                          Seu navegador nao suporta a reproducao deste video.
                        </video>
                      )}
                    </div>

                    <div className="support-video-body">
                      <div className="support-video-copy">
                        <h2>{video.title || "Video sem titulo"}</h2>
                      </div>

                      <div className="support-video-actions">
                        <button type="button" onClick={() => handleShare(video)}>
                          Compartilhar
                        </button>
                        <button
                          type="button"
                          className="is-primary"
                          onClick={() => handleDownload(video)}
                        >
                          {isLink ? "Abrir / baixar" : "Baixar video"}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="support-videos-empty-state">
              Nenhum video de apoio foi configurado no momento. Procure o super administrador.
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

export default VideosApoio;
